/**
 * AudioWaveform — Audio player con forma d'onda animata
 * Sostituisce il player audio nativo per i messaggi vocali
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, Mic, ChevronDown, ChevronUp } from 'lucide-react';
import type { MessageAttachment } from '../../lib/stores/teamChatStore';
import { AUDIO_TRANSCRIPTION_LEGACY_ENABLED } from '@/lib/featureFlags';

interface AudioWaveformProps {
  att: MessageAttachment;
  isOwn: boolean;
}

// Genera barre pseudo-casuali basate sul nome file (deterministiche)
function generateBars(seed: string, count: number): number[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  const bars: number[] = [];
  for (let i = 0; i < count; i++) {
    hash = ((hash * 16807) + 12345) | 0;
    const val = Math.abs(hash % 100);
    bars.push(20 + (val / 100) * 80); // 20-100% range
  }
  return bars;
}

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export function AudioWaveform({ att, isOwn }: AudioWaveformProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(att.duration || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showTranscription, setShowTranscription] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animRef = useRef<number>(0);

  const BAR_COUNT = 32;
  const bars = useRef(generateBars(att.name || att.url, BAR_COUNT)).current;

  const accentColor = isOwn ? 'rgba(255,255,255,0.8)' : '#2383E2';
  const barBg = isOwn ? 'rgba(255,255,255,0.25)' : 'rgba(55,53,47,0.12)';
  const textColor = isOwn ? 'rgba(255,255,255,0.7)' : '#787774';

  useEffect(() => {
    const audio = new Audio(att.url);
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    });

    audio.addEventListener('ended', () => {
      setPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    });

    return () => {
      audio.pause();
      audio.src = '';
      cancelAnimationFrame(animRef.current);
    };
  }, [att.url]);

  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !playing) return;
    if (audio.duration && isFinite(audio.duration)) {
      setProgress(audio.currentTime / audio.duration);
      setCurrentTime(audio.currentTime);
    }
    animRef.current = requestAnimationFrame(updateProgress);
  }, [playing]);

  useEffect(() => {
    if (playing) {
      animRef.current = requestAnimationFrame(updateProgress);
    }
    return () => cancelAnimationFrame(animRef.current);
  }, [playing, updateProgress]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  }, [playing]);

  const handleBarClick = useCallback((barIndex: number) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || !isFinite(audio.duration)) return;
    const newTime = (barIndex / BAR_COUNT) * audio.duration;
    audio.currentTime = newTime;
    setProgress(barIndex / BAR_COUNT);
    setCurrentTime(newTime);
  }, []);

  return (
    <div className="flex flex-col gap-1" style={{ minWidth: 200, maxWidth: 280 }}>
      <div className="flex items-center gap-2">
        {/* Play/Pause button */}
        <button
          onClick={togglePlay}
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
          style={{ backgroundColor: accentColor }}
        >
          {playing ? (
            <Pause className="w-4 h-4" style={{ color: isOwn ? '#2383E2' : '#fff' }} strokeWidth={2} fill="currentColor" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" style={{ color: isOwn ? '#2383E2' : '#fff' }} strokeWidth={2} fill="currentColor" />
          )}
        </button>

        {/* Waveform bars */}
        <div className="flex items-center gap-px flex-1" style={{ height: 32 }}>
          {bars.map((height, i) => {
            const filled = i / BAR_COUNT <= progress;
            return (
              <button
                key={i}
                onClick={() => handleBarClick(i)}
                className="flex-1 rounded-full transition-colors"
                style={{
                  height: `${height}%`,
                  backgroundColor: filled ? accentColor : barBg,
                  minWidth: 2,
                  maxWidth: 4,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Time display */}
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px]" style={{ color: textColor }}>
          {playing ? formatDuration(currentTime) : formatDuration(duration)}
        </span>
        <Mic className="w-3 h-3" style={{ color: textColor }} strokeWidth={1.5} />
      </div>

      {/* Transcription toggle */}
      {AUDIO_TRANSCRIPTION_LEGACY_ENABLED && att.transcription && (
        <div>
          <button
            onClick={() => setShowTranscription(!showTranscription)}
            className="flex items-center gap-1 text-[11px] hover:opacity-80 transition-opacity"
            style={{ color: accentColor }}
          >
            {showTranscription ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Trascrizione
          </button>
          {showTranscription && (
            <p className="text-[12px] mt-1 px-2 py-1.5 rounded-md italic leading-relaxed" style={{ backgroundColor: 'rgba(55,53,47,0.06)' }}>
              {att.transcription}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
